import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { sendErrorReport } from 'shared/helpers'
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notice,
  InputErrorMessage,
  Notification,
  MultiTagInput,
  LabelTag
} from 'shared/components'
import { errorMsg } from 'shared/constants'

import './styles.scss'
import { addCustomFieldEnum } from 'src/product/actions'

const CustomFieldEnumForm = ({
  closeCb,
  confirmCb,
  companyID,
  productCustomField,
  initialEnums = []
}) => {
  const productCustomFieldID = productCustomField.id

  const [dirty, setDirty] = useState(false)
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [enums, setEnums] = useState({
    tags: [],
    tagError: '',
    value: ''
  })

  const isFormValid = () => {
    if (!enums.tags.length) {
      setEnums({ ...enums, tagError: errorMsg.required })
      return false
    }
    return true
  }

  const handleAddField = async () => {
    setLoading(false)
    const allEnums = enums.tags || []

    try {
      await Promise.all(
        allEnums.map(sf =>
          addCustomFieldEnum({ value: sf, product_custom_field: productCustomFieldID }, companyID)
        )
      )
      Notification(
        'success',
        __('Changes saved successfully'),
        __('Allowed values added to custom field')
      )
      confirmCb()
    } catch (err) {
      sendErrorReport(err, 'Cannot add allowed values to custom field', allEnums)
      setLoading(false)
      Notification(
        'error',
        __('Your changes were not saved'),
        __('There was an error while saving your changes')
      )
    }
    return true
  }

  const handleSubmit = e => {
    e.preventDefault()
    const isValid = isFormValid()
    if (!isValid) {
      return false
    }

    setLoading(true)

    handleAddField()
    return true
  }

  const handleClose = () => {
    if (!dirty) {
      return closeCb()
    }
    return setDirtyFormAlertDisplay(true)
  }

  const title = __('Add allowed values')

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      title={title}
      size='sm'
    >
      <div className='CustomFieldForm'>
        <Notice>
          {__('Define a list of values that can be assigned to this custom field on a license.')}
        </Notice>
        <form className='custom-field-form' onSubmit={handleSubmit}>
          <div className='form-row'>
            <div className='existing-values'>
              {initialEnums.map((value, index) => (
                <LabelTag key={`${value}-${index}`} label={{ name: value }} />
              ))}
            </div>
            <Label text={__('Add new values')} inputId='name' />
            <MultiTagInput
              tags={enums.tags}
              existingTags={initialEnums}
              onTagSubmit={val => {
                setDirty(true)
                setEnums(val)
              }}
              isDisabled={isLoading}
              id='allowed-values-input'
            />
            <InputErrorMessage text={enums.tagError} />
          </div>
        </form>
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  )
}

CustomFieldEnumForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  initialEnums: PropTypes.arrayOf(PropTypes.string),
  companyID: PropTypes.number.isRequired,
  productCustomField: PropTypes.object.isRequired,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

CustomFieldEnumForm.defaultProps = {
  fieldToEdit: null
}

export default CustomFieldEnumForm
